import React from 'react';
import withSorting from 'components/pure/sortable-tables/withSorting';
import {List} from 'immutable';
import SortableTableHeader from 'components/pure/sortable-tables/SortableTableHeader';
import {RepProfileAndName} from 'components/coaching/landing-pages/helpers/ProfileAndName';
import listAsString from 'utils/listAsString';
import {PercentChangeIndicator} from 'components/coaching/landing-pages/helpers/PercentChangeIndicator';
import CheckIcon from 'material-ui/svg-icons/navigation/check';
import ClearIcon from 'material-ui/svg-icons/content/clear';
import './PastPlansList.styl';
import formatSecondsShort from 'utils/formatSecondsShort';
import {
  compareMoments,
  compareNumeric,
  compareReps,
  compareSkills,
} from 'components/coaching/landing-pages/helpers/tableSorts';
import PlanProgress from 'components/coaching/landing-pages/helpers/PlanProgress';
import average from 'utils/average';

const headerData = [
  {label: 'Rep', column: 'rep', className: 'active-rep'},
  {label: 'Skill', column: 'skill', className: 'skill'},
  {label: 'Plan Start', column: 'planStart', className: 'plan-start-col'},
  {label: 'Plan End', column: 'planEnd', className: 'plan-end-col'},
  {label: 'Reviews', column: 'planProgress', className: 'reviews'},
  {
    label: 'Change In Skill(s) During Plan',
    column: 'changeInScore',
    className: 'since-plan-start',
  },
  {label: 'Goal(s) Met', column: 'goalsMet', className: 'goals-met-col'},
];

class PastPlansList extends React.PureComponent {
  render() {
    const {
      sortedData,
      sortColumn,
      sortDirection,
      onSort,
      startDate,
      endDate,
      fromPath,
      coachId,
      managerId,
      adminId,
    } = this.props;

    return (
      <span className="past-plans-list">
        <table className="rep-list">
          <thead>
            <tr>
              {headerData.map(data => (
                <th key={data.label} className={data.className}>
                  <SortableTableHeader
                    label={data.label}
                    renderColumn={data.column}
                    onClick={() => onSort(data.column, sortDirection)}
                    sortDirection={sortDirection}
                    sortColumn={sortColumn}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {this.renderTableRows(
              sortedData,
              coachId,
              managerId,
              adminId,
              startDate,
              endDate,
              fromPath
            )}
          </tbody>
        </table>
      </span>
    );
  }

  renderTableRows(sortedData, coachId, managerId, adminId, startDate, endDate, fromPath) {
    return sortedData.map(
      ({rep, skill, planStart, planEnd, planProgress, changeInScore, goalsMet, plan}) => {
        const planId = plan.id;
        const end = formatMoment(planEnd);
        const skillName = plan.livecoaching ? 'Live Coaching' : skill;
        return (
          <tr key={planId}>
            <td>
              <RepProfileAndName
                rep={rep}
                coachId={coachId}
                managerId={managerId}
                adminId={adminId}
                fromPath={fromPath}
              />
            </td>
            <td>{skillName}</td>
            <td>{formatMoment(planStart)}</td>
            <td>{end}</td>
            <td>
              {planProgress && (
                <PlanProgress
                  repId={rep.id}
                  planId={planId}
                  renderPlanProgress={() => renderPlanProgress(planProgress)}
                  makePlanPopoverContent={() => makePlanPopoverContent(plan, planProgress, end)}
                  coachId={coachId}
                  managerId={managerId}
                  adminId={adminId}
                />
              )}
            </td>
            <td>
              <PercentChangeIndicator avg={changeInScore} />
            </td>
            <td className="goals-met-cell">
              {goalsMet == null ? '' : goalsMet ? <CheckIcon /> : <ClearIcon />}
            </td>
          </tr>
        );
      }
    );
  }
}

function formatMoment(m) {
  return m == null ? '' : m.format('MM/DD/YYYY');
}

function makePlanPopoverContent(plan, planProgress, planEnd) {
  const completedReviews = plan.reviews.filter(r => r.status === 'COMPLETED').size;
  const scheduleTimeInMinutes = plan.schedule && plan.schedule.durationInMinutes;
  const reviewTimeInMinutes = formatSecondsShort(
    completedReviews * scheduleTimeInMinutes * 60,
    true,
    true,
    false
  );

  return (
    <div>
      {planProgress && (
        <p>
          {planProgress.completed || 0}
          {planProgress.total && ` out of ${planProgress.total}`} planned meetings took place.
        </p>
      )}
      {planEnd && <p>Plan ended on {planEnd}</p>}
      <p>{reviewTimeInMinutes} of meetings took place.</p>
    </div>
  );
}

function renderPlanProgress(planProgress) {
  if (!planProgress) {
    return '';
  }
  if (!planProgress.total) {
    return `${planProgress.completed || 0} Meetings`;
  }
  return `${planProgress.completed || 0} / ${planProgress.total} Meetings`;
}

function gatherData(props) {
  const {pastPlans} = props;
  if (!pastPlans || pastPlans.size === 0) {
    return List();
  }
  return pastPlans.map(map => {
    const velocity = map.get('velocity');
    const plan = map.get('plan');
    const planEnd = plan.completedAt;

    // note: this is wrong, but only ~10% of plans are multiskill, and this column is relatively hidden, and this
    // is probably usually right :)
    const averageGoal = average(plan.contentDetails.map(d => d.skillGoal));
    const planScore = velocity.lastScore;
    const goalsMet = planScore == null ? null : planScore >= averageGoal;

    return {
      rep: plan.recipient.info,
      skill: listAsString(plan.contentDetails.map(detail => detail.skillName || detail.custom)),
      planStart: plan.created,
      planEnd,
      planProgress: velocity && velocity.planProgress,
      changeInScore: velocity && velocity.changeInScore,
      goalsMet,
      // Not rendered.
      plan,
    };
  });
}

export default withSorting(
  PastPlansList,
  props => gatherData(props),
  {
    rep: compareReps,
    skill: compareSkills,
    planStart: (l, r) => compareMoments(l.planStart, r.planStart),
    planEnd: (l, r) => compareMoments(l.planEnd, r.planEnd),
    planProgress: (l, r) => compareNumeric(r.planProgress.completed, l.planProgress.completed),
    changeInScore: (l, r, sortDirection) =>
      compareNumeric(r.changeInScore, l.changeInScore, sortDirection !== 'asc'),
    goalsMet: (l, r, sortDirection) =>
      compareNumeric(l.goalsMet, r.goalsMet, sortDirection === 'asc'),
  },
  {
    sortColumn: 'planStart',
    sortDirection: 'asc',
  }
);
